import { useRef, useState, ChangeEvent, KeyboardEvent, useEffect } from "react";
import {
  IconArrowDropDown,
  IconArrowDropUp,
  KEY_VALUES,
  MenuListItem,
  MENU_PLACEMENT,
  Input,
} from "@cdkglobal/radial";
import {
  ISelectableOption,
  RecordsSearchFilterResponse,
  RenderAutoCompleteMenuProps,
} from "../../../../types";
import {
  DEFAULT_ISelectableOption,
  EDITABLE_AUTOCOMPLETE_FIELD_KEYS,
  LOADING_DEFAULT_ISelectableOption,
  NO_RESULTS_DEFAULT_ISelectableOption,
} from "../../constants";
import {
  StyledAutocomplete,
  StyledInlineInput,
} from "../../../../utils/styled";
import {
  convertToOption,
  getRecordsBySearchFilter,
  getTitleFromTargetPropertyKey,
  getCompanyIdByStoreId,
  getCompanyIdByStoreIdFromTypeAheadResponse,
  getEvaluatedTypeAheadRequest,
  fetchTypeAheadSearchResponse,
  dispatchUpdateField,
  getSelectableTypeAheadOptions,
} from "../../helpers";
import { getEmptyFieldsMessage } from "../../validations";
import { uniqueId } from "underscore";
import {
  customTypeAheadEvaluations,
  customTypeAheadDefaultIdentifiers,
  iscustomTypeAheadResultsEvaluated,
  customEvaluations,
} from "../../evaluations";
import { useUserContext } from "../../../../contexts/UserContext";
import { useIntl } from "react-intl";

export default function RenderAutoCompleteMenu({
  title,
  propertyKey,
  rowIndex,
  propertySubKey,
  inputValue: inputValueOption,
  companyKey,
  isRequired,
  isDisabled,
  error,
  message,
  typeAheadRequest,
  evalMethod,
  evalTypeAheadMethods,
  dependentFields,
  dynamicDependentFieldsAndValues,
  existingGroupIdKey,
  existingGroupIdValue,
  isDialogField,
  selectedTabSectionKey,
  rowDataReducerState,
  dispatch,
  isInline = false,
}: Readonly<RenderAutoCompleteMenuProps>): JSX.Element {
  const [defaultOptions, setDefaultOptions] = useState<ISelectableOption[]>([
    LOADING_DEFAULT_ISelectableOption(),
  ]);
  const targetRef = useRef<HTMLInputElement>(null);
  const dimensionRef = useRef<HTMLLabelElement>(null);
  const menuPlacement =
    targetRef.current &&
    window.innerHeight - targetRef.current.getBoundingClientRect().bottom < 200
      ? MENU_PLACEMENT.TOP_START
      : MENU_PLACEMENT.BOTTOM_START;
  const intl = useIntl();
  const initialOption = convertToOption(inputValueOption);
  const [selectedOption, setSelectedOption] =
    useState<ISelectableOption>(initialOption);
  const [inputValue, setInputValue] = useState<string>(initialOption.label);

  const [options, setOptions] = useState<ISelectableOption[]>([
    LOADING_DEFAULT_ISelectableOption(),
  ]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [searchResponse, setSearchResponse] =
    useState<RecordsSearchFilterResponse>({ message: "", data: [] });
  const { BACKSPACE, ENTER, SPACE, TAB } = KEY_VALUES;
  const {
    iamUserState,
    evaluatedResults: existingEvaluatedResults,
    setEvaluatedResults,
    selected,
  } = useUserContext();
  const fieldKey = propertySubKey
    ? `${rowDataReducerState.schema.title}-${propertySubKey}`
    : `${rowDataReducerState.schema.title}-${propertyKey}`;

  const canAcceptUserInput =
    EDITABLE_AUTOCOMPLETE_FIELD_KEYS.includes(fieldKey);

  useEffect(() => {
    if (
      inputValueOption === undefined ||
      inputValueOption === null ||
      inputValueOption === ""
    ) {
      setSelectedOption(DEFAULT_ISelectableOption);
      setInputValue("");
    } else if (selectedOption.value !== inputValueOption) {
      const option = convertToOption(inputValueOption);
      setSelectedOption(option);
      setInputValue(option.label);
    }
  }, [inputValueOption]);

  const handleOpen = (): void => {
    if (!isDisabled) {
      setIsOpen(true);
    }
  };

  const handleClose = (): void => {
    setIsOpen(false);
  };

  const handleMenu = async (): Promise<void> => {
    if (isDisabled) {
      return;
    }
    setIsOpen((isOpen) => !isOpen);
    await fetchSearchOptions();
  };

  const fetchSearchOptions = async (): Promise<void> => {
    if (
      existingGroupIdKey &&
      (existingGroupIdValue === "" || existingGroupIdValue === null)
    ) {
      const removed$FromKey = existingGroupIdKey.startsWith("$")
        ? existingGroupIdKey.slice(1)
        : existingGroupIdKey;
      const requiredField = getTitleFromTargetPropertyKey({
        targetKey: removed$FromKey,
        sourceKey: propertyKey,
        rowIndex,
        rowDataReducerState,
      });

      const defaultOption = {
        label: `Select ${requiredField}`,
        value: "",
        disabled: true,
      };

      setDefaultOptions([defaultOption]);
      setOptions([defaultOption]);
      return;
    }

    const emptyRequiredFieldsMessage = getEmptyFieldsMessage(
      dynamicDependentFieldsAndValues,
      propertyKey,
      rowIndex,
      rowDataReducerState,
    );
    if (emptyRequiredFieldsMessage) {
      setDefaultOptions([emptyRequiredFieldsMessage]);
      setOptions([emptyRequiredFieldsMessage]);
      return;
    }

    setDefaultOptions([LOADING_DEFAULT_ISelectableOption()]);
    setOptions([LOADING_DEFAULT_ISelectableOption()]);
    handleTypeAheadRequest();
  };

  const handleTypeAheadRequest = async () => {
    let updatedTypeAheadRequest = typeAheadRequest;

    if (evalTypeAheadMethods) {
      if (
        iscustomTypeAheadResultsEvaluated(
          evalTypeAheadMethods,
          existingEvaluatedResults,
        )
      ) {
        updatedTypeAheadRequest = getEvaluatedTypeAheadRequest(
          existingEvaluatedResults,
          typeAheadRequest,
        );
      } else {
        const updatedEvaluatedResults = await customTypeAheadEvaluations(
          evalTypeAheadMethods,
          typeAheadRequest,
        );
        const combinedResults = {
          ...existingEvaluatedResults,
          ...updatedEvaluatedResults,
        };
        setEvaluatedResults(combinedResults);
        updatedTypeAheadRequest = getEvaluatedTypeAheadRequest(
          combinedResults,
          typeAheadRequest,
        );
      }
    }

    const { options, response } = await getRecordsBySearchFilter(
      updatedTypeAheadRequest,
      evalTypeAheadMethods,
    );
    const filteredOptions = getSelectableTypeAheadOptions({
      propertyKey,
      options,
      selected,
      rowDataReducerState,
    });

    if (
      filteredOptions.length === 0 &&
      updatedTypeAheadRequest.defaultIdentifier
    ) {
      const defaultStoreOptions = customTypeAheadDefaultIdentifiers(
        updatedTypeAheadRequest.defaultIdentifier,
        iamUserState.extendedAccountInfo,
      );
      const filteredDefaultOptions = getSelectableTypeAheadOptions({
        propertyKey,
        options: defaultStoreOptions,
        selected,
        rowDataReducerState,
      });
      setDefaultOptions(filteredDefaultOptions);
      setOptions(filteredDefaultOptions);
      return;
    }

    setSearchResponse(response);
    if (filteredOptions.length > 0) {
      setDefaultOptions(filteredOptions);
      setOptions(filteredOptions);
    } else {
      setDefaultOptions([NO_RESULTS_DEFAULT_ISelectableOption()]);
      setOptions([NO_RESULTS_DEFAULT_ISelectableOption()]);
    }
  };

  const handleSelect = (option: ISelectableOption): void => {
    if (isOptionSelected(option)) return;
    handleSelectedOption(option);
    setInputValue(option.label);
    setSelectedOption(option);
    setOptions(defaultOptions);
    handleClose();
    (targetRef.current as HTMLInputElement).focus();
  };

  const handleSelectedOption = (option: ISelectableOption): void => {
    dispatch({
      type: "UPDATE_FIELD",
      payload: {
        propertyKey,
        newFieldValue: option.value,
        rowIndex,
        propertySubKey,
        isDialogField,
        selectedTabSectionKey,
      },
    });
    updateCompanyValue(option);
    handleDependentFields();
    if (evalMethod) {
      customEvaluations({
        evalMethod,
        propertyKey,
        propertySubKey,
        rowIndex,
        inputValue: option.value,
        searchResponse,
        selectedTabSectionKey,
        rowDataReducerState,
        dispatch,
      });
    }
  };

  const handleDependentFields = (): void => {
    if (dependentFields) {
      dependentFields.forEach((targetKey) => {
        dispatchUpdateField({
          propertyKey,
          propertySubKey,
          rowIndex,
          newFieldValue: "",
          isDialogField,
          selectedTabSectionKey,
          dispatch,
          targetFieldKey: targetKey,
        });
      });
    }
  };

  const updateCompanyValue = (option: ISelectableOption) => {
    if (companyKey) {
      const companyIdValue =
        searchResponse.data.length === 0
          ? getCompanyIdByStoreId(
              option.value as string,
              iamUserState.extendedAccountInfo,
            )
          : getCompanyIdByStoreIdFromTypeAheadResponse(
              option.value as string,
              searchResponse,
              typeAheadRequest.body.responseFields,
            );

      dispatchUpdateField({
        propertyKey,
        propertySubKey,
        rowIndex,
        newFieldValue: companyIdValue,
        isDialogField,
        selectedTabSectionKey,
        targetFieldKey: companyKey,
        dispatch,
      });
    }
  };

  useEffect(() => {
    // for interco setup when we are populating store based on group
    if (isDisabled) {
      updateCompanyValue(selectedOption);
    }
  }, [selectedOption.value]);

  const findOptions = (event: ChangeEvent<HTMLInputElement>): void => {
    if (isDisabled) {
      return;
    }
    const inputText = event.target.value;
    setInputValue(inputText);
    setIsOpen(true);
    const results = defaultOptions.filter(
      (opt) =>
        opt.label.toLowerCase().startsWith(inputText.toLowerCase()) ||
        opt.label.toLowerCase().includes(inputText.toLowerCase()),
    );
    if (results.length > 0) {
      setOptions(results);
    } else if (canAcceptUserInput && inputText.endsWith("*")) {
      const newFieldValue = convertToOption(inputText);
      handleSelect(newFieldValue);
    } else {
      setOptions([NO_RESULTS_DEFAULT_ISelectableOption()]);
    }
    if (inputText.length === 0) {
      setOptions(defaultOptions);
      setIsOpen(true);
    }
  };

  // v8: onInputClear expects PointerEvent<HTMLButtonElement> | KeyboardEvent<HTMLButtonElement>
  const handleClearInput = (
    event:
      | React.PointerEvent<HTMLButtonElement>
      | React.KeyboardEvent<HTMLButtonElement>,
  ): void => {
    event.preventDefault();
    setInputValue("");
    handleSelectedOption(DEFAULT_ISelectableOption);
    setOptions(defaultOptions);
    handleDependentFields();
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (isDisabled) {
      return;
    }
    const target = e.target as HTMLInputElement;
    if (e.key === ENTER) {
      if (canAcceptUserInput && target.value.endsWith("*")) {
        const newFieldValue = convertToOption(target.value);
        handleSelectedOption(newFieldValue);
        setInputValue(target.value);
        handleMenu();
      } else if (options.length > 0 && !options[0].disabled) {
        handleSelectedOption(options[0]);
        setInputValue(options[0].label);
        handleMenu();
      }
    }
    if (e.key === SPACE) handleMenu();
    if (e.key === BACKSPACE && selectedOption.label !== "") {
      // call directly without event — v8 signature mismatch
      setInputValue("");
      handleSelectedOption(DEFAULT_ISelectableOption);
      setOptions(defaultOptions);
      handleDependentFields();
    }
    if (e.key === TAB && selectedOption.label === "") {
      setInputValue("");
      setOptions(defaultOptions);
    }
  };

  const isOptionSelected = (option: ISelectableOption): boolean => {
    return (
      selectedOption.label === option.label ||
      (selectedOption.value !== "" && selectedOption.value === option.value)
    );
  };

  useEffect(() => {
    const handleEvalMethod = async () => {
      if (evalMethod) {
        let updatedResponse = searchResponse;
        if (existingEvaluatedResults[propertyKey]) {
          updatedResponse = existingEvaluatedResults[
            propertyKey
          ] as RecordsSearchFilterResponse;
        } else {
          const response = await fetchTypeAheadSearchResponse(
            typeAheadRequest,
            evalMethod,
          );
          updatedResponse = response;
          setEvaluatedResults({
            ...existingEvaluatedResults,
            [propertyKey]: updatedResponse,
          });
        }
        customEvaluations({
          evalMethod,
          propertyKey,
          propertySubKey,
          rowIndex,
          inputValue: inputValueOption,
          searchResponse: updatedResponse,
          selectedTabSectionKey,
          rowDataReducerState,
          dispatch,
        });
      }
    };
    handleEvalMethod();
  }, []);

  const id =
    rowIndex !== undefined && propertySubKey
      ? `-${rowIndex}-${propertySubKey}`
      : "";
  const formattedId = isInline
    ? `${propertyKey}${id}-inline-typeahead-dropdown`
    : `${propertyKey}${id}-typeahead-dropdown`;

  const InputComponent = isInline ? StyledInlineInput : Input;

  return (
    <>
      <div data-testid={formattedId}>
        <InputComponent
          dataTestId={formattedId}
          hasClearButton={selectedOption.label !== "" && !isDisabled}
          icon={
            !isDisabled ? (
              isOpen ? (
                <IconArrowDropUp />
              ) : (
                <IconArrowDropDown />
              )
            ) : undefined
          }
          id={uniqueId(formattedId)}
          label={title}
          labelRef={dimensionRef}
          name="typeahead-dropdown"
          onChange={findOptions}
          onClick={isDisabled ? undefined : handleMenu}
          onInputClear={handleClearInput}
          onKeyDown={handleKeyPress}
          placeholder={isDisabled ? title : intl.formatMessage(
            { id: "placeholder.select", defaultMessage: "Select {item}" },
            { item: title },
          )}
          isRequired={isRequired && !isDisabled}
          isDisabled={isDisabled}
          ref={targetRef}
          value={inputValue ?? ""}
          enableCustomValidation={true}
          hasError={error}
          errorMessage={message}
          {...(isInline && { hideLabel: true })}
          // tooltip={description}
        />
      </div>
      {!isDisabled && (
        <StyledAutocomplete
          dataTestId="autocomplete-menu"
          dimensionRef={dimensionRef}
          labelRef={targetRef}
          isAuto={false}
          placement={menuPlacement}
          onClose={handleClose}
          onChange={findOptions}
          onOpen={handleOpen}
          isOpen={isOpen}
          onUnselect={selectedOption.label === ""}
          style={
            isInline
              ? {}
              : {
                  width: dimensionRef?.current?.offsetWidth,
                  maxWidth: dimensionRef?.current?.offsetWidth,
                }
          }
        >
          {options.map((option, ind) => (
            <MenuListItem
              title={option.label}
              dataTestId={`autocomplete-menu-list-item-${ind}`}
              key={uniqueId(JSON.stringify(option.value))}
              isSelected={isOptionSelected(option)}
              onClick={() => handleSelect(option)}
              isDisabled={option.disabled}
              showDivider={ind !== options.length - 1}
            >
              {option.label}
            </MenuListItem>
          ))}
        </StyledAutocomplete>
      )}
    </>
  );
}
